import React, { useEffect, useState, useCallback, useRef } from 'react';
import Form from 'react-bootstrap/Form';
import { Trash2, Plus, Pencil } from 'lucide-react';

import ButtonAction from '../../atoms/buttonAction/view';
import { getSpendTypes } from '../../../usecases/spend-type-usecase';
import { calculateNetValue, formatPercent } from '../../../services/calculations';
import { formatMoney } from '../../../utils/formating';
import Chip from '../../atoms/chip/container';
import NewSpendType from '../../organisms/NewSpendType/view';
import { dispatchCustomEvent, listener } from '../../../utils/dispatch';
import './style.css';
import ButtonPrimary from '../../atoms/ButtonPrimary/view';


function ServiceLineInput({ onServiceLinesChange, serviceLines = [], initialSpendTypes = null, onRefreshSpendTypes }) {
    const [currentService, setCurrentService] = useState({ spend_type_id: '', amount: 0, description: '' });
    const [spendTypes, setSpendTypes] = useState(initialSpendTypes || []);
    const [editSpendType, setEditSpendType] = useState(null);

    const serviceLinesRef = useRef(serviceLines);
    const onServiceLinesChangeRef = useRef(onServiceLinesChange);

    useEffect(() => {
        serviceLinesRef.current = serviceLines;
        onServiceLinesChangeRef.current = onServiceLinesChange;
    }, [serviceLines, onServiceLinesChange]);

    const fetchLatestSpendTypes = useCallback(() => {
        getSpendTypes(1, 1000)
            .then((res) => {
                const list = res?.data || [];
                setSpendTypes(list);
            })
            .catch(() => {
                /* ignore error */
            });
    }, []);

    useEffect(() => {
        if (initialSpendTypes) {
            setSpendTypes(initialSpendTypes);
        } else {
            fetchLatestSpendTypes();
        }
    }, [initialSpendTypes, fetchLatestSpendTypes]);

    useEffect(() => {
        const unbind = listener('reload-spend-types', () => {
            getSpendTypes(1, 1000)
                .then((res) => {
                    const list = res?.data || [];
                    setSpendTypes(list);

                    setCurrentService(prev => {
                        if (!prev.spend_type_id) return prev;
                        const matched = list.find(st => Number(st.id) === Number(prev.spend_type_id));
                        if (matched) {
                            return { ...prev, spend_type_data: matched };
                        }
                        return { spend_type_id: '', amount: 0, description: '' };
                    });

                    const currentLines = serviceLinesRef.current || [];
                    if (currentLines.length > 0) {
                        const updatedLines = currentLines.filter(line => {
                            return list.some(st => Number(st.id) === Number(line.spend_type_id));
                        }).map(line => {
                            const matched = list.find(st => Number(st.id) === Number(line.spend_type_id));
                            return {
                                ...line,
                                spend_type_data: matched
                            };
                        });
                        if (onServiceLinesChangeRef.current) {
                            onServiceLinesChangeRef.current(updatedLines);
                        }
                    }
                })
                .catch(() => {
                    /* ignore error */
                });
        });
        return () => {
            if (unbind) unbind();
        };
    }, []);

    const handleServiceChange = (event) => {
        const { name, value, type } = event.target;
        if (name === "spend_type_id") {
            const selectedType = spendTypes?.find(type => type.id === parseInt(value));
            setCurrentService((prevState) => ({
                ...prevState,
                spend_type_data: selectedType
            }));
        }
        setCurrentService((prev) => ({
            ...prev,
            [name]: type === 'number' ? parseFloat(value) : value,
        }));
    };

    const handleOpenCreateModal = (e) => {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
            e.stopPropagation();
        }
        setEditSpendType(null);
        dispatchCustomEvent('modalnewSpendType-service-line', { eventKey: 'newSpendType-service-line' });
    };

    const handleOpenEditModal = (e) => {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
            e.stopPropagation();
        }
        const selectedType = spendTypes?.find(type => type.id === parseInt(currentService.spend_type_id));
        if (!selectedType) return;
        setEditSpendType(selectedType);
        dispatchCustomEvent('modalnewSpendType-service-line', { eventKey: 'newSpendType-service-line' });
    };

    const handleSpendTypeSuccess = useCallback(async () => {
        fetchLatestSpendTypes();
        if (onRefreshSpendTypes) await onRefreshSpendTypes();
        setEditSpendType(null);
    }, [onRefreshSpendTypes, fetchLatestSpendTypes]);

    const addServiceLine = () => {
        if (currentService.spend_type_id && currentService.amount > 0) {
            const newLines = [...serviceLines, { ...currentService, id: Date.now() }];
            onServiceLinesChange(newLines);
            setCurrentService({ spend_type_id: '', amount: 0, description: '' });
        }
    };

    const removeServiceLine = (idToRemove) => {
        const newLines = serviceLines.filter(service => service.id !== idToRemove);
        onServiceLinesChange(newLines);
    };

    const toggleChipInServiceLine = (serviceId, chipType) => {
        const newLines = serviceLines.map(service => {
            if (service.id === serviceId) {
                const updatedService = { ...service };
                if (chipType === 'tax' && updatedService.spend_type_data?.tax) {
                    updatedService.spend_type_data = {
                        ...updatedService.spend_type_data,
                        tax: { ...updatedService.spend_type_data.tax, disabled: !updatedService.spend_type_data.tax.disabled }
                    };
                } else if (chipType === 'retention' && updatedService.spend_type_data?.retention) {
                    updatedService.spend_type_data = {
                        ...updatedService.spend_type_data,
                        retention: { ...updatedService.spend_type_data.retention, disabled: !updatedService.spend_type_data.retention.disabled }
                    };
                }
                return updatedService;
            }
            return service;
        });
        onServiceLinesChange(newLines);
    };

    return (
        <div className="service-line-input-container">
            <h4>Agregar Servicios</h4>
            <div className="service-input-form">
                <Form.Group id="spend-type" className="m-1" style={{ flex: 1.5 }}>
                    <Form.Label>Tipo de Gasto</Form.Label>
                    <div className="d-flex align-items-center gap-1">
                        <Form.Select
                            name="spend_type_id"
                            id="spend_type_id"
                            onChange={handleServiceChange}
                            value={currentService.spend_type_id}
                            style={{ flex: 1, minWidth: "250px" }}
                        >
                            <option value="">Seleccione un tipo</option>
                            {spendTypes?.map((item) => (
                                <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                        </Form.Select>
                        <ButtonAction
                            title={<Plus size={16} />}
                            onClick={handleOpenCreateModal}
                            type="action"
                            style={{ minWidth: '38px', height: '38px' }}
                        />
                        {currentService.spend_type_id && !String(currentService.spend_type_id).startsWith('new_') && (
                            <ButtonAction
                                title={<Pencil size={14} />}
                                onClick={handleOpenEditModal}
                                type="action"
                                style={{ minWidth: '38px', height: '38px' }}
                            />
                        )}
                    </div>
                </Form.Group>
                <Form.Group className="m-1" style={{ flex: 1 }}>
                    <Form.Label>Monto</Form.Label>
                    <Form.Control
                        type="number"
                        name="amount"
                        value={currentService.amount || ''}
                        onChange={handleServiceChange}
                        placeholder="Monto"
                        style={{ minWidth: "250px" }}
                    />
                </Form.Group>
                <Form.Group className="m-1" style={{ flex: 2 }}>
                    <Form.Label>Descripción</Form.Label>
                    <Form.Control
                        name="description"
                        value={currentService.description}
                        onChange={handleServiceChange}
                        placeholder="Descripción del servicio"
                        style={{ minWidth: "250px" }}
                    />
                </Form.Group>
            </div>
            <ButtonPrimary type="add" onClick={addServiceLine} disabled={!currentService.spend_type_id || currentService.amount <= 0}>
                Agregar Servicio
            </ButtonPrimary>

            {serviceLines.length > 0 && (
                <div className="service-lines-list">
                    <h5>Servicios Agregados:</h5>
                    <ul>
                        {serviceLines.map((service) => (
                            <li key={service.id}>
                                <div className='service-line-info'>
                                    <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                        <strong>{service.spend_type_data?.name}</strong>
                                        {service.spend_type_data?.isNew ? (
                                            <Chip type="warning">¡Se creará automáticamente!</Chip>
                                        ) : (
                                            <Chip type="success">¡Ya existe en el POS!</Chip>
                                        )}
                                    </div>
                                    <p>Bruto: {formatMoney(Math.round(service.amount))}</p>
                                    {service.tax > 0 && <p className='tax-value'>IVA: {formatMoney(Math.round(service.tax))}</p>}
                                    {service.retention > 0 && (
                                        <p className='ret-value'>
                                            Retención ({formatPercent(service.spend_type_data?.retention?.value)}): -{formatMoney(Math.round(service.retention))}
                                        </p>
                                    )}
                                    <p>
                                        <strong>
                                            Neto: {formatMoney(Math.round(calculateNetValue(service.amount, service.retention)))}
                                        </strong>
                                    </p>
                                </div>
                                <div className='chip-box'>
                                    {service.spend_type_data?.tax && (
                                        <div className={`chip ${service.spend_type_data.tax.disabled ? 'chip-disabled' : ''}`}>
                                            <span>{service.spend_type_data.tax.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => toggleChipInServiceLine(service.id, 'tax')}
                                                className={`remove-chip-btn ${service.spend_type_data.tax.disabled ? 'activate-chip-btn' : ''}`}
                                            >
                                                {service.spend_type_data.tax.disabled ? '+' : 'x'}
                                            </button>
                                        </div>
                                    )}
                                    {service.spend_type_data?.retention && (
                                        <div className={`chip ${service.spend_type_data.retention.disabled ? 'chip-disabled' : ''}`}>
                                            <span>{service.spend_type_data.retention.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => toggleChipInServiceLine(service.id, 'retention')}
                                                className={`remove-chip-btn ${service.spend_type_data.retention.disabled ? 'activate-chip-btn' : ''}`}
                                            >
                                                {service.spend_type_data.retention.disabled ? '+' : 'x'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <ButtonAction onClick={() => removeServiceLine(service.id)} className="remove-service-btn">
                                    <Trash2 size={16} />
                                </ButtonAction>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <NewSpendType
                id="service-line"
                writeMode={editSpendType ? 'edit' : 'create'}
                writeId={editSpendType?.id}
                initialData={editSpendType}
                onSuccess={handleSpendTypeSuccess}
                hideButton={true}
            />
        </div>
    );
}

export default ServiceLineInput;
